import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** When set, replaces the default full-page error card with a scoped fallback
   *  — used to isolate non-critical sections so a crash in one widget doesn't
   *  take down the whole page. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught:", error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    if (this.props.fallback !== undefined) return this.props.fallback;

    const message = this.state.error.message || "Unexpected error";

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md rounded-plane bg-white p-8 text-center shadow-plane">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          </div>
          <h1 className="mt-5 text-lg font-semibold tracking-tight text-slate-900">
            Something went wrong
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            The page crashed. Try reloading. If it keeps happening, let us
            know.
          </p>
          <pre className="mt-5 max-h-40 overflow-auto rounded-lg bg-slate-50 p-3 text-left font-mono text-[11px] text-slate-600">
            {message}
          </pre>
          <div className="mt-5 flex justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={this.reset}
            >
              Try again
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
